
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Images, MessageSquare, Database } from 'lucide-react';
import RealMediaViewer from './RealMediaViewer';
import { supabase } from '@/integrations/supabase/client';
import { debugLogger } from '@/utils/debugLogger';
import { useAdminFilter } from '@/contexts/AdminFilterContext';

type MediaTab = 'profile' | 'chat';

const AdminMediaManagement = () => {
  const [selectedTab, setSelectedTab] = useState<MediaTab>('profile');
  const { userTypeFilter } = useAdminFilter();
  const [totalProfilePhotos, setTotalProfilePhotos] = useState(0);
  const [totalChatMedia, setTotalChatMedia] = useState(0);
  const [loading, setLoading] = useState(true);

  // Fetch counts via RPC
  useEffect(() => {
    const fetchCounts = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase.rpc('get_admin_media_counts', {
          user_type: userTypeFilter
        });

        if (error) throw error;

        if (data) {
          const counts = data as { profilePhotos: number; chatMedia: number };
          setTotalProfilePhotos(counts.profilePhotos || 0);
          setTotalChatMedia(counts.chatMedia || 0);
          debugLogger.debug('[AdminMediaManagement] Counts loaded:', counts);
        }
      } catch (error) {
        debugLogger.error('[AdminMediaManagement] Error fetching counts:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCounts();
  }, [userTypeFilter]);

  const tabs = [
    {
      key: 'profile' as const,
      label: 'Fotos de Perfil',
      icon: Images,
      count: totalProfilePhotos
    },
    {
      key: 'chat' as const,
      label: 'Mídia de Chat',
      icon: MessageSquare,
      count: totalChatMedia
    }
  ];

  if (loading && totalProfilePhotos === 0 && totalChatMedia === 0) {
    return (
      <div className="px-4 space-y-4">
        <div className="h-10 bg-white/10 rounded animate-pulse"></div>
        <div className="h-64 bg-white/10 rounded animate-pulse"></div>
      </div>
    );
  }

  return (
    <div className="px-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Images className="h-5 w-5" />
            Gestão de Mídias Reais
          </h2>
          <p className="text-white/60 text-sm">
            Visualize todas as mídias registradas no banco de dados
          </p>
        </div>
      </div>

      {/* Tab Selector */}
      <div className="grid grid-cols-2 gap-3">
        {tabs.map(tab => {
          const Icon = tab.icon;
          const isSelected = selectedTab === tab.key;
          
          return (
            <Button
              key={tab.key}
              onClick={() => setSelectedTab(tab.key)}
              className={`
                h-auto p-4 flex flex-col items-center gap-2 transition-all
                ${isSelected 
                  ? 'bg-luxury-red text-white hover:bg-luxury-red/90' 
                  : 'bg-white/10 text-white hover:bg-white/20'
                }
              `}
            >
              <Icon className="h-6 w-6" />
              <div className="text-center">
                <p className="font-medium">{tab.label}</p>
                <p className="text-xs opacity-60">
                  {loading ? 'Carregando...' : `${tab.count} ${tab.count !== 1 ? 'perfis' : 'perfil'}`}
                </p>
              </div>
            </Button>
          );
        })}
      </div>

      {/* Content */}
      <Card className="bg-white/5 border-white/10">
        <CardHeader className="pb-3">
          <CardTitle className="text-white flex items-center justify-between">
            <span>{tabs.find(t => t.key === selectedTab)?.label}</span>
            <div className="flex items-center gap-2 text-sm font-normal text-white/60">
              <Database className="h-4 w-4" />
              <span>
                {loading ? 'Carregando...' : `${tabs.find(t => t.key === selectedTab)?.count || 0} perfis`}
              </span>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <RealMediaViewer mediaType={selectedTab} />
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminMediaManagement;
